import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Prenotazione } from '../hooks/usePrenotazioni';

interface CalendarViewProps {
  prenotazioni: Prenotazione[];
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
}

const CalendarView: React.FC<CalendarViewProps> = ({ prenotazioni, onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get prenotazioni for a specific date
  const getPrenotazioniForDate = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return prenotazioni.filter(p => p.data === dateString);
  };

  // Get status summary for a date
  const getStatusSummary = (date: Date) => {
    const prenotazioniForDate = getPrenotazioniForDate(date);
    const confermate = prenotazioniForDate.filter(p => p.stato === 'confermata').length;
    const inAttesa = prenotazioniForDate.filter(p => p.stato === 'in_attesa').length;
    const rifiutate = prenotazioniForDate.filter(p => p.stato === 'rifiutata').length;
    
    return { confermate, inAttesa, rifiutate, total: prenotazioniForDate.length };
  };

  // Calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Navigation
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Check if date is today
  const isToday = (date: Date) => isSameDay(date, new Date());

  // Get dot color based on status
  const getDotColor = (date: Date) => {
    const { confermate, inAttesa, rifiutate } = getStatusSummary(date);
    
    if (rifiutate > 0) return 'bg-red-500';
    if (confermate > 0) return 'bg-green-500';
    if (inAttesa > 0) return 'bg-orange-500';
    return '';
  };

  // Get border color for selected date
  const getBorderColor = (date: Date) => {
    const hasBookings = getPrenotazioniForDate(date).length > 0;
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    
    if (isSelected) return 'ring-2 ring-orange-500';
    if (hasBookings) return 'ring-1 ring-orange-300';
    return '';
  };

  return (
    <div className="flex gap-6">
      {/* Calendar */}
      <div className="flex-1">
        <div className="bg-white rounded-lg shadow p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-6 w-6 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Calendario Prenotazioni</h2>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-lg font-medium text-gray-900">
                {format(currentMonth, 'MMMM yyyy', { locale: it })}
              </span>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((day) => (
              <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 uppercase tracking-wider">
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const statusSummary = getStatusSummary(day);
              const hasBookings = statusSummary.total > 0;
              const dotColor = getDotColor(day);
              const borderColor = getBorderColor(day);

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => onDateSelect(day)}
                  className={`
                    relative p-3 min-h-[80px] cursor-pointer transition-all
                    ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                    ${borderColor}
                    ${isToday(day) ? 'ring-2 ring-red-500' : ''}
                    hover:bg-gray-50
                  `}
                >
                  {/* Date number */}
                  <div className={`
                    text-sm font-medium mb-1
                    ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    ${isToday(day) ? 'text-red-600' : ''}
                  `}>
                    {format(day, 'd')}
                  </div>

                  {/* Booking indicator */}
                  {hasBookings && (
                    <div className="text-xs text-gray-600 mb-1">
                      {statusSummary.total} pren.
                    </div>
                  )}

                  {/* Status dot */}
                  {dotColor && (
                    <div className={`w-2 h-2 rounded-full ${dotColor} mx-auto`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Legenda</h3>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Confermate</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-gray-600">Da accettare</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">Rifiutate</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-red-500 rounded"></div>
                <span className="text-gray-600">Oggi</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-80">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Seleziona una data</h3>
          
          {selectedDate ? (
            <div>
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
                </h4>
              </div>
              
              {getPrenotazioniForDate(selectedDate).length > 0 ? (
                <div className="space-y-3">
                  {getPrenotazioniForDate(selectedDate).map((prenotazione) => (
                    <div key={prenotazione.id} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{prenotazione.nome}</h5>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          prenotazione.stato === 'confermata' ? 'bg-green-100 text-green-800' :
                          prenotazione.stato === 'rifiutata' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {prenotazione.stato === 'confermata' ? 'Confermata' :
                           prenotazione.stato === 'rifiutata' ? 'Rifiutata' : 'In Attesa'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Ora: {prenotazione.ora}</div>
                        <div>Persone: {prenotazione.persone}</div>
                        <div>Email: {prenotazione.email}</div>
                        <div>Tel: {prenotazione.telefono}</div>
                        {prenotazione.note && (
                          <div>Note: {prenotazione.note}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  Nessuna prenotazione per questa data
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              Seleziona una data per vedere le prenotazioni
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView; 